import React, { useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { getFullAssetURI } from '../../../utils/utils';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import { ImagePickerMedia } from '../../../stores/reducers/media';
import { MediaBackground } from '../../../stores/reducers/content';

interface ImagePickerProps {
	open: boolean;
	label: string;
	close: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	setImage: (url: string) => void;
	selectedImage: string;
	logo: string;
	thumbnail?: string;
	clearBgImage: () => void;
	externalImages: ImagePickerMedia[];
	backgroundType: string;
	deleteImage: (image: ImagePickerMedia) => void;
	defaultBackgrounds?: MediaBackground[];
}

export default function ImagePicker(props: ImagePickerProps) {
	const {
		open,
		label,
		close,
		setImage,
		selectedImage,
		logo,
		thumbnail,
		clearBgImage,
		externalImages,
		backgroundType,
		deleteImage,
		defaultBackgrounds,
	} = props;

	const [style, setStyle] = useState<React.CSSProperties>({});
	const imagePickerWrapper = useRef<HTMLDivElement | null>(null);
	const imagePickerContainer = useRef<HTMLDivElement | null>(null);

	/*
		A way to show a fixed position popup in the canvas without jumping,
		alligned to the bottom of the picker, and accounting for a change
		in list length.
	*/
	useLayoutEffect(() => {
		if (open && imagePickerWrapper.current && imagePickerContainer.current) {
			const header = document.querySelector('.meeting-header');

			if (header) {
				const headerDim = header.getBoundingClientRect();
				const imageWrapDim = imagePickerWrapper.current.getBoundingClientRect();
				const imageContainDim = imagePickerContainer.current.getBoundingClientRect();

				if (imageContainDim.bottom < imageWrapDim.bottom) {
					const proposedTop = imageWrapDim.top - imageContainDim.height;

					if (proposedTop > headerDim.bottom + 1) {
						setStyle({
							top: imageWrapDim.top - imageContainDim.height,
						});
					} else {
						setStyle({});
					}
				}
			}
		} else {
			setStyle({});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultBackgrounds?.length, externalImages?.length, open]);

	return (
		<div className={classNames('image-picker-wrapper', { open })} ref={imagePickerWrapper}>
			{open && <div className="image-picker-background" onClick={close}></div>}
			<div className={classNames('image-picker-container', { open })} style={style} ref={imagePickerContainer}>
				<div className="image-label"><label>{label}</label></div>
				<div className="images-container">
					<div key={-1} onClick={() => clearBgImage()} className={classNames('image no-image', { selected: backgroundType === 'color' })}>
						No Image
					</div>
					{logo
						&& <div className="image">
							<img
								onClick={() => setImage(logo)}
								style={{ maxWidth: '100%', objectFit: 'contain', backgroundColor: '#000000' }}
								className={classNames({ selected: backgroundType === 'image' && logo === selectedImage })}
								key={logo}
								src={logo}
								alt={logo}
							/>
						</div>
					}
					{thumbnail && (
						<div className="image">
							<img
								onClick={() => setImage(thumbnail ?? '')}
								style={{ maxWidth: '100%', objectFit: 'cover', backgroundColor: '#000000' }}
								className={classNames({ selected: backgroundType === 'image' && thumbnail === selectedImage })}
								key={thumbnail}
								src={thumbnail}
								alt={thumbnail}
							/>
						</div>
					)}
					{defaultBackgrounds && defaultBackgrounds.map((defaultBackground, index) => (
						<div key={index} className="image">
							<img
								onClick={() => setImage(defaultBackground.image)}
								className={classNames({ selected: backgroundType === 'image' && defaultBackground.image === selectedImage })}
								key={index}
								src={getFullAssetURI(defaultBackground.thumb)}
								alt={defaultBackground.thumb}
							/>
						</div>
					))}
					{externalImages.map((image: ImagePickerMedia, index: number) => (
						<div key={index} className="image">
							<img
								onClick={() => setImage(image.url)}
								className={classNames({ selected: backgroundType === 'image' && image.url === selectedImage })}
								key={index}
								src={getFullAssetURI(image.url)}
								alt={image.url}
							/>
							<button className="remove-image"
								onClick={() => {
									if (typeof deleteImage === typeof Function) {
										deleteImage(image);
									}
								}}
							><Icon name={ICONS.ADD_CLOSE_CIRCLE} size={15} color={COLORS.WHITE}></Icon></button>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}
